import { useState, useEffect } from 'react';
import { api } from '@/shared/lib/api';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import { trackEvent } from '@/shared/lib/monitoring';

interface Props {
  onClose: () => void;
  featureName?: string;
}

const STARS_PLANS = [
  { period: 'monthly', label: '1 месяц', price: 299, badge: '' },
  { period: 'quarterly', label: '3 месяца', price: 799, badge: 'Выгоднее' },
  { period: 'yearly', label: '1 год', price: 2499, badge: 'Лучшая цена' },
];

const RUBLE_PLANS = [
  { period: 'monthly', label: '1 месяц', price: 299, badge: '' },
  { period: 'quarterly', label: '3 месяца', price: 699, badge: 'Выгоднее' },
  { period: 'yearly', label: '1 год', price: 1990, badge: 'Лучшая цена' },
];

const FEATURES = [
  '✅ Все курсы (A1–C2, Top 500)',
  '✅ Безлимитный AI-помощник',
  '✅ Все 8 тем оформления',
  '✅ Грамматические упражнения',
  '✅ RPG персонаж и предметы',
  '✅ Приоритетная поддержка',
];

export function PaywallModal({ onClose, featureName }: Props) {
  const [selectedPeriod, setSelectedPeriod] = useState('quarterly');
  const [paymentMethod, setPaymentMethod] = useState<'stars' | 'yookassa'>('stars');
  const [loading, setLoading] = useState(false);
  const [promoMode, setPromoMode] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');
  const { load: reloadSub, setPremium } = useSubscriptionStore();

  useEffect(() => {
    trackEvent('paywall_viewed', { feature: featureName ?? null });
  }, []);

  const plans = paymentMethod === 'stars' ? STARS_PLANS : RUBLE_PLANS;

  const handlePurchase = async () => {
    setLoading(true);
    trackEvent('purchase_started', { method: paymentMethod, period: selectedPeriod });
    try {
      if (paymentMethod === 'stars') {
        const data = await api.post<any>('/subscription/invoice/stars', { period: selectedPeriod });
        const tg = (window as any).Telegram?.WebApp;
        if (tg?.openInvoice) {
          tg.openInvoice(data.payload, (status: string) => {
            if (status === 'paid') {
              trackEvent('purchase_completed', { method: 'stars', period: selectedPeriod });
              reloadSub();
              onClose();
            }
          });
        } else {
          alert(`DEV: инвойс на ${data.stars} Stars за ${selectedPeriod}`);
        }
      } else {
        const data = await api.post<any>('/subscription/invoice/yookassa', { period: selectedPeriod });
        trackEvent('purchase_started_yookassa', { period: selectedPeriod });
        const tg = (window as any).Telegram?.WebApp;
        if (tg?.openLink) {
          tg.openLink(data.confirmation_url);
        } else {
          window.open(data.confirmation_url, '_blank');
        }
        alert('После оплаты Premium активируется автоматически в течение минуты.');
        onClose();
      }
    } catch (err: any) {
      alert(err.message || 'Ошибка при создании платежа');
    } finally {
      setLoading(false);
    }
  };

  const handlePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoError('');
    setPromoSuccess('');
    setLoading(true);
    try {
      const result = await api.post<any>('/subscription/promo', { code: promoCode });
      setPromoSuccess(result.message);
      if (result.expires_at) setPremium(result.expires_at);
      trackEvent('promo_code_applied', { code: promoCode });
      await reloadSub();
      setTimeout(onClose, 1500);
    } catch (err: any) {
      setPromoError(err.message || 'Ошибка активации промокода');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'var(--theme-bg-elevated)',
        borderRadius: '24px 24px 0 0',
        padding: '24px 20px 40px',
        width: '100%',
        maxWidth: 480,
        maxHeight: '90vh',
        overflowY: 'auto',
        animation: 'slideUp 0.3s ease',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 48 }}>⭐</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--theme-text)', marginBottom: 4 }}>
            2EnBota Premium
          </div>
          {featureName && (
            <div style={{ fontSize: 14, color: 'var(--theme-text-secondary)' }}>
              Для доступа к «{featureName}» нужен Premium
            </div>
          )}
        </div>

        {/* Список фич */}
        <div style={{
          background: 'var(--theme-bg)',
          borderRadius: 12,
          padding: '12px 16px',
          marginBottom: 20,
        }}>
          {FEATURES.map((f, i) => (
            <div key={i} style={{
              fontSize: 14, color: 'var(--theme-text)',
              padding: '4px 0',
            }}>{f}</div>
          ))}
        </div>

        {!promoMode ? (
          <>
            {/* Выбор способа оплаты */}
            <div style={{
              display: 'flex', gap: 8, marginBottom: 16,
              background: 'var(--theme-bg)', borderRadius: 12, padding: 4,
            }}>
              <button
                onClick={() => setPaymentMethod('stars')}
                style={{
                  flex: 1, padding: '10px',
                  background: paymentMethod === 'stars' ? 'var(--theme-primary)' : 'transparent',
                  color: paymentMethod === 'stars' ? '#fff' : 'var(--theme-text-secondary)',
                  border: 'none', borderRadius: 8,
                  fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                ⭐ Telegram Stars
              </button>
              <button
                onClick={() => setPaymentMethod('yookassa')}
                style={{
                  flex: 1, padding: '10px',
                  background: paymentMethod === 'yookassa' ? 'var(--theme-primary)' : 'transparent',
                  color: paymentMethod === 'yookassa' ? '#fff' : 'var(--theme-text-secondary)',
                  border: 'none', borderRadius: 8,
                  fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                💳 Рублями
              </button>
            </div>

            {/* Тарифы */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {plans.map(plan => (
                <button key={plan.period}
                  onClick={() => setSelectedPeriod(plan.period)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 16px',
                    background: selectedPeriod === plan.period
                      ? 'var(--theme-primary)' : 'var(--theme-bg)',
                    color: selectedPeriod === plan.period ? '#fff' : 'var(--theme-text)',
                    border: selectedPeriod === plan.period
                      ? '2px solid var(--theme-primary)'
                      : '2px solid var(--theme-border)',
                    borderRadius: 12,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{plan.label}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {plan.badge && (
                      <span style={{
                        fontSize: 11, background: '#FFD700', color: '#000',
                        borderRadius: 6, padding: '2px 6px', fontWeight: 700,
                      }}>{plan.badge}</span>
                    )}
                    <span style={{ fontWeight: 700 }}>
                      {paymentMethod === 'stars' ? `⭐ ${plan.price}` : `${plan.price} ₽`}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            <button onClick={handlePurchase} disabled={loading} style={{
              width: '100%', padding: '16px',
              background: loading ? 'var(--theme-border)' : 'var(--theme-primary)',
              color: 'var(--theme-primary-text)', border: 'none', borderRadius: 12,
              fontSize: 16, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
              marginBottom: 12,
            }}>
              {loading
                ? 'Загрузка...'
                : paymentMethod === 'stars'
                  ? 'Оплатить Telegram Stars ⭐'
                  : 'Оплатить картой 💳'}
            </button>

            <button onClick={() => setPromoMode(true)} style={{
              width: '100%', padding: '12px',
              background: 'transparent', color: 'var(--theme-primary)',
              border: 'none', fontSize: 14, cursor: 'pointer',
            }}>
              У меня есть промокод
            </button>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <input
                value={promoCode}
                onChange={e => setPromoCode(e.target.value.toUpperCase())}
                placeholder="ВВЕДИТЕ ПРОМОКОД"
                style={{
                  width: '100%', padding: '14px', boxSizing: 'border-box',
                  background: 'var(--theme-bg)', color: 'var(--theme-text)',
                  border: '2px solid var(--theme-border)', borderRadius: 12,
                  fontSize: 16, fontWeight: 600, letterSpacing: 2,
                  textAlign: 'center',
                }}
              />
              {promoError && (
                <div style={{ color: '#ff4444', fontSize: 13, marginTop: 6, textAlign: 'center' }}>
                  {promoError}
                </div>
              )}
              {promoSuccess && (
                <div style={{ color: '#44bb44', fontSize: 13, marginTop: 6, textAlign: 'center' }}>
                  {promoSuccess}
                </div>
              )}
            </div>
            <button onClick={handlePromo} disabled={loading || !promoCode.trim()} style={{
              width: '100%', padding: '16px',
              background: loading ? 'var(--theme-border)' : 'var(--theme-primary)',
              color: 'var(--theme-primary-text)', border: 'none', borderRadius: 12,
              fontSize: 16, fontWeight: 700, cursor: 'pointer',
              marginBottom: 12,
            }}>
              {loading ? 'Активация...' : 'Активировать'}
            </button>
            <button onClick={() => { setPromoMode(false); setPromoError(''); }} style={{
              width: '100%', padding: '12px',
              background: 'transparent', color: 'var(--theme-text-secondary)',
              border: 'none', fontSize: 14, cursor: 'pointer',
            }}>
              Назад к планам
            </button>
          </>
        )}
      </div>
    </div>
  );
}
